"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { SignalSlashIcon } from "@heroicons/react/24/outline";
import CustomTooltip from "@/components/utils/tooltip/CustomTooltip";
import { fetchGithubPreview, type GithubPreviewResponse } from "@/services/api/github-preview-api";
interface GithubPreviewStatusBadgeProps {
  readonly href: string;
  readonly initialPreview?: GithubPreviewResponse | null | undefined;
  readonly compact?: boolean | undefined;
}
type BadgeTone = "green" | "purple" | "red" | "gray" | "amber";
interface BadgeViewModel {
  readonly label: string;
  readonly detail?: string | undefined;
  readonly tone: BadgeTone;
  readonly loading?: boolean | undefined;
}
interface GithubPreviewUrlInfo {
  readonly url: URL;
  readonly kind: "issue" | "pull";
}
const parseGithubPreviewUrl = (href: string): URL | null => {
  try {
    const url = new URL(href);
    const hostname = url.hostname.replace(/^www\./i, "").toLowerCase();
    if (hostname !== "github.com") {
      return null;
    }
    const [,, kind, number] = url.pathname.split("/").filter(Boolean);
    if (kind !== "issues" && kind !== "pull" || !number) {
      return null;
    }
    return url;
  } catch {
    return null;
  }
};
const parseGithubPreviewUrlInfo = (href: string): GithubPreviewUrlInfo | null => {
  const url = parseGithubPreviewUrl(href);
  if (!url) {
    return null;
  }
  const [,, kind] = url.pathname.split("/").filter(Boolean);
  if (kind === "pull") {
    return {
      url,
      kind: "pull"
    };
  }
  if (kind === "issues") {
    return {
      url,
      kind: "issue"
    };
  }
  return null;
};
const getBadgeViewModel = (preview: GithubPreviewResponse): BadgeViewModel => {
  if (preview.type === "github.issue") {
    switch (preview.state) {
      case "open":
        return {
          label: "Open",
          tone: "green"
        };
      case "closed_completed":
        return {
          label: "Completed",
          tone: "purple"
        };
      case "closed_not_planned":
        return {
          label: "Not planned",
          tone: "gray"
        };
      case "closed":
        return {
          label: "Closed",
          tone: "red"
        };
    }
  }
  if (preview.state === "merged") {
    return {
      label: "Merged",
      tone: "purple"
    };
  }
  if (preview.state === "draft") {
    return {
      label: "Draft",
      tone: "gray"
    };
  }
  if (preview.state === "closed") {
    return {
      label: "Closed",
      tone: "red"
    };
  }
  if (preview.reviewState === "changes_requested") {
    return {
      label: "Changes requested",
      tone: "red"
    };
  }
  if (preview.reviewState === "approved") {
    return {
      label: "Approved",
      tone: "green"
    };
  }
  if (preview.mergeableState && preview.mergeableState !== "unknown") {
    return {
      label: "Open",
      detail: preview.mergeableState.replaceAll("_", " "),
      tone: preview.mergeableState === "clean" ? "green" : "amber"
    };
  }
  return {
    label: "Open",
    tone: "green"
  };
};
const TONE_CLASSES: Record<BadgeTone, string> = {
  green: "tw-border-emerald-400/40 tw-bg-emerald-950/90 tw-text-emerald-100 tw-shadow-emerald-950/40",
  purple: "tw-border-violet-400/40 tw-bg-violet-950/90 tw-text-violet-100 tw-shadow-violet-950/40",
  red: "tw-border-iron-600/45 tw-bg-iron-950/70 tw-text-iron-400 tw-shadow-black/20",
  gray: "tw-border-iron-500/50 tw-bg-iron-950/90 tw-text-iron-100 tw-shadow-black/40",
  amber: "tw-border-amber-400/40 tw-bg-amber-950/90 tw-text-amber-100 tw-shadow-amber-950/40"
};
const VISIBLE_REFRESH_INTERVAL_MS = 30 * 1000;
type GithubStatusState = {
  readonly type: "idle";
} | {
  readonly type: "loading";
} | {
  readonly type: "success";
  readonly preview: GithubPreviewResponse;
} | {
  readonly type: "error";
  readonly message: string;
};
const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  return "Failed to get status";
};
const getStatusViewModel = (status: GithubStatusState): BadgeViewModel => {
  if (status.type === "success") {
    return getBadgeViewModel(status.preview);
  }
  if (status.type === "error") {
    return {
      label: "Status unavailable",
      tone: "red"
    };
  }
  return {
    label: "Loading status",
    tone: "gray",
    loading: true
  };
};
const getBadgeTitle = (status: GithubStatusState, viewModel: BadgeViewModel, detail: string | undefined): string => {
  if (status.type === "error") {
    return "Status unavailable";
  }
  if (detail) {
    return `${viewModel.label} · ${detail}`;
  }
  return viewModel.label;
};
export default function GithubPreviewStatusBadge(t0) {
  const $ = _c(36);
  const {
    href,
    initialPreview,
    compact: t1
  } = t0;
  const compact = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== href) {
    t2 = parseGithubPreviewUrlInfo(href);
    $[0] = href;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const githubInfo = t2;
  const badgeRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  let t3;
  if ($[2] !== initialPreview) {
    t3 = () => initialPreview ? {
      type: "success",
      preview: initialPreview
    } : {
      type: "idle"
    };
    $[2] = initialPreview;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [status, setStatus] = useState(t3);
  let t4;
  let t5;
  if ($[4] !== githubInfo) {
    t4 = () => {
      if (!githubInfo) {
        setIsVisible(false);
        return;
      }
      const badge = badgeRef.current;
      if (!badge || typeof IntersectionObserver === "undefined") {
        setIsVisible(true);
        return;
      }
      const observer = new IntersectionObserver(t6 => {
        const [entry] = t6;
        return setIsVisible(entry?.isIntersecting === true);
      }, {
        threshold: 0.1
      });
      observer.observe(badge);
      return () => observer.disconnect();
    };
    t5 = [githubInfo];
    $[4] = githubInfo;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[7] !== githubInfo || $[8] !== initialPreview || $[9] !== isVisible) {
    t6 = () => {
      if (!githubInfo) {
        setStatus({
          type: "idle"
        });
        return;
      }
      let active = true;
      if (initialPreview) {
        setStatus({
          type: "success",
          preview: initialPreview
        });
        return () => {
          active = false;
        };
      }
      if (!isVisible) {
        return () => {
          active = false;
        };
      }
      setStatus({
        type: "loading"
      });
      fetchGithubPreview(githubInfo.url.toString()).then(response => {
        if (active) {
          setStatus({
            type: "success",
            preview: response
          });
        }
      }).catch(error => {
        if (active) {
          setStatus({
            type: "error",
            message: getErrorMessage(error)
          });
        }
      });
      return () => {
        active = false;
      };
    };
    t7 = [githubInfo, initialPreview, isVisible];
    $[7] = githubInfo;
    $[8] = initialPreview;
    $[9] = isVisible;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[12] !== githubInfo || $[13] !== isVisible) {
    t8 = () => {
      if (!githubInfo || !isVisible) {
        return;
      }
      let active_0 = true;
      const refreshStatus = () => {
        fetchGithubPreview(githubInfo.url.toString(), {
          bypassCache: true
        }).then(response_0 => {
          if (active_0) {
            setStatus({
              type: "success",
              preview: response_0
            });
          }
        }).catch(error_0 => {
          if (active_0) {
            setStatus({
              type: "error",
              message: getErrorMessage(error_0)
            });
          }
        });
      };
      const interval = globalThis.setInterval(refreshStatus, VISIBLE_REFRESH_INTERVAL_MS);
      return () => {
        active_0 = false;
        globalThis.clearInterval(interval);
      };
    };
    t9 = [githubInfo, isVisible];
    $[12] = githubInfo;
    $[13] = isVisible;
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  useEffect(t8, t9);
  if (!githubInfo) {
    return null;
  }
  let detail;
  let t10;
  let viewModel;
  if ($[16] !== compact || $[17] !== status) {
    viewModel = getStatusViewModel(status);
    detail = compact ? undefined : viewModel.detail;
    t10 = getBadgeTitle(status, viewModel, detail);
    $[16] = compact;
    $[17] = status;
    $[18] = detail;
    $[19] = t10;
    $[20] = viewModel;
  } else {
    detail = $[18];
    t10 = $[19];
    viewModel = $[20];
  }
  const title = t10;
  const t11 = `tw-pointer-events-auto tw-absolute tw-right-2 tw-top-2 tw-z-20 tw-inline-flex tw-max-w-[calc(100%-1rem)] tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-solid tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-semibold tw-leading-none tw-shadow-lg tw-backdrop-blur-md ${TONE_CLASSES[viewModel.tone]}`;
  let t12;
  if ($[21] !== viewModel.loading) {
    t12 = viewModel.loading && <span className="tw-h-2 tw-w-2 tw-animate-spin tw-rounded-full tw-border tw-border-solid tw-border-current tw-border-r-transparent" />;
    $[21] = viewModel.loading;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== status.type || $[24] !== viewModel.label) {
    t13 = status.type === "error" ? <SignalSlashIcon className="tw-h-3.5 tw-w-3.5" aria-hidden="true" strokeWidth={2} /> : <span className="tw-line-clamp-1 tw-capitalize">{viewModel.label}</span>;
    $[23] = status.type;
    $[24] = viewModel.label;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== detail) {
    t14 = detail && <span className="tw-hidden tw-font-medium tw-normal-case tw-opacity-70 sm:tw-inline">{detail}</span>;
    $[26] = detail;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t11 || $[29] !== t12 || $[30] !== t13 || $[31] !== t14 || $[32] !== title) {
    t15 = <span ref={badgeRef} className={t11} data-testid="github-preview-status-badge" aria-label={title}>{t12}{t13}{t14}</span>;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
    $[32] = title;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  const badge_0 = t15;
  if (status.type !== "error") {
    return badge_0;
  }
  let t16;
  if ($[34] !== badge_0) {
    t16 = <CustomTooltip content="Status unavailable" placement="top" delayShow={200}>{badge_0}</CustomTooltip>;
    $[34] = badge_0;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  return t16;
}